import { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import roundAddAPhoto from '@iconify/icons-ic/round-add-a-photo';
import { WhiteLabelSectionRoot } from './whiteLabelSectionCommon';
import { VisuallyHiddenInput } from '../../../../../../components/common/VisuallyHiddenInput';
import { WhiteLabelLogoField } from '../../../../../../gql/graphql';
import type { GetPublicWhiteLabelSettingsQuery } from '../../../../../../gql/graphql';
/** WL row shape for logo slots — from `publicWhiteLabelSettings`. */
type WhiteLabelSettingsWithLogos = NonNullable<
  GetPublicWhiteLabelSettingsQuery['publicWhiteLabelSettings']
>;

/** One logo slot — same selected fields as `logo`, `favicon`, etc. on the query. */
type WhiteLabelLogoSlotFile = NonNullable<WhiteLabelSettingsWithLogos['logo']>;

const ACCEPT_MIME = [
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'image/webp',
  // ICO is needed for favicon uploads; browsers report either of these MIME types.
  'image/x-icon',
  'image/vnd.microsoft.icon',
];
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB — not exposed on GraphQL; matches backend upload limit

/** Pending logo edits — applied on main form Save (parent runs mutations). */
export type PendingLogoChange =
  | { type: 'upload'; file: File; preview: string; filename: string }
  | { type: 'remove' };

/**
 * Single source: enum value, GraphQL settings field key, UI label, and display order.
 * Typo in `settingsKey` fails at `settings[slot.settingsKey]` (must match the WL row selection).
 */
export const WHITE_LABEL_LOGO_SLOTS = [
  {
    logoField: WhiteLabelLogoField.Logo,
    settingsKey: 'logo',
    label: 'Main Logo',
  },
  {
    logoField: WhiteLabelLogoField.Favicon,
    settingsKey: 'favicon',
    label: 'Favicon',
  },
  {
    logoField: WhiteLabelLogoField.MobileLogo,
    settingsKey: 'mobileLogo',
    label: 'Mobile Logo',
  },
  {
    logoField: WhiteLabelLogoField.SquareLogo,
    settingsKey: 'squareLogo',
    label: 'Square Logo',
  },
  {
    logoField: WhiteLabelLogoField.HeaderLogo,
    settingsKey: 'headerLogo',
    label: 'Header Logo',
  },
  {
    logoField: WhiteLabelLogoField.FooterLogo,
    settingsKey: 'footerLogo',
    label: 'Footer Logo',
  },
] as const;

interface LogoSlotProps {
  label: string;
  current?: WhiteLabelLogoSlotFile | null;
  pendingChange: PendingLogoChange | null;
  onPendingChange: (change: PendingLogoChange | null) => void;
}

function LogoSlot({
  label,
  current,
  pendingChange,
  onPendingChange,
}: LogoSlotProps) {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const serverLogoData = current?.logoData ?? null;
  const preview =
    pendingChange?.type === 'upload'
      ? pendingChange.preview
      : pendingChange?.type === 'remove'
      ? null
      : serverLogoData;

  const handleFile = useCallback(
    (file: File) => {
      if (!ACCEPT_MIME.includes(file.type)) {
        enqueueSnackbar('Only PNG, JPEG, SVG, WEBP, ICO files are allowed', {
          variant: 'warning',
        });
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        enqueueSnackbar('File is too large (max 2 MB)', {
          variant: 'warning',
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        // readAsDataURL yields a string; guard avoids assertions if the API shape changes.
        if (typeof result !== 'string') return;
        onPendingChange({
          type: 'upload',
          file,
          preview: result,
          filename: file.name,
        });
      };
      reader.readAsDataURL(file);
    },
    [onPendingChange, enqueueSnackbar],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  /** True when this slot has a saved S3 row (Remove marks delete until Save). */
  const hasPersistedServerFile =
    current != null && current.id != null && String(current.id).length > 0;

  const handleRemove = useCallback(() => {
    // Undo pending remove — show server image again
    if (pendingChange?.type === 'remove') {
      onPendingChange(null);
      return;
    }
    // Drop pending upload only — nothing persisted yet
    if (pendingChange?.type === 'upload') {
      onPendingChange(null);
      return;
    }
    if (!hasPersistedServerFile) {
      onPendingChange(null);
      return;
    }
    onPendingChange({ type: 'remove' });
  }, [pendingChange, hasPersistedServerFile, onPendingChange]);

  const hasPreviewImage = Boolean(preview);

  const dropZone = (
    <DropZoneRoot
      isDragging={dragging}
      hasImage={hasPreviewImage}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {preview ? (
        <PreviewImage
          key={`${label}-${pendingChange?.type ?? 'server'}-${
            preview.startsWith('data:') ? 'inline' : 'remote'
          }`}
          src={preview}
          alt={label}
        />
      ) : (
        <EmptySlotBody>
          <EmptySlotIconWrap>
            <Icon icon={roundAddAPhoto} width={28} height={28} />
          </EmptySlotIconWrap>
          <Typography variant='caption' display='block'>
            Upload
          </Typography>
        </EmptySlotBody>
      )}
    </DropZoneRoot>
  );

  return (
    <SlotColumn>
      <SlotLabel variant='caption' color='text.secondary'>
        {label}
      </SlotLabel>

      {dropZone}

      {/* Visually hidden; opened via inputRef from drop zone / buttons (shared a11y pattern). */}
      <VisuallyHiddenInput
        ref={inputRef}
        type='file'
        accept={ACCEPT_MIME.join(',')}
        onChange={onInputChange}
      />

      {preview || pendingChange?.type === 'remove' ? (
        <SlotActionRow>
          <RemoveLogoButton
            size='small'
            color='inherit'
            startIcon={<DeleteOutlineIcon fontSize='small' />}
            onClick={handleRemove}
          >
            {pendingChange?.type === 'remove' ? 'Undo remove' : 'Remove'}
          </RemoveLogoButton>

          {preview ? (
            <UpdateLogoButton
              size='small'
              variant='outlined'
              onClick={() => inputRef.current?.click()}
            >
              Update
            </UpdateLogoButton>
          ) : pendingChange?.type === 'remove' ? (
            <ChooseFileButton
              size='small'
              variant='text'
              onClick={() => inputRef.current?.click()}
            >
              Choose file
            </ChooseFileButton>
          ) : null}
        </SlotActionRow>
      ) : (
        <ChooseFileButton
          size='small'
          variant='text'
          onClick={() => inputRef.current?.click()}
        >
          Choose file
        </ChooseFileButton>
      )}
    </SlotColumn>
  );
}

// ---------------------------------------------------------------------------

function currentLogoForUploadField(
  settings: WhiteLabelSettingsWithLogos | null | undefined,
  logoField: WhiteLabelLogoField,
): WhiteLabelLogoSlotFile | null {
  if (!settings) return null;
  const slot = WHITE_LABEL_LOGO_SLOTS.find((s) => s.logoField === logoField);
  if (!slot) return null;
  const file = settings[slot.settingsKey];
  return file ?? null;
}

export interface LogoSectionProps {
  settings: WhiteLabelSettingsWithLogos | null | undefined;
  pendingChanges: Partial<Record<WhiteLabelLogoField, PendingLogoChange>>;
  onPendingChange: (
    field: WhiteLabelLogoField,
    change: PendingLogoChange | null,
  ) => void;
}

export function LogoSection({
  settings,
  pendingChanges,
  onPendingChange,
}: LogoSectionProps) {
  return (
    <WhiteLabelSectionRoot>
      <SectionTitle variant='h6'>Logos</SectionTitle>

      <SlotsGrid>
        {WHITE_LABEL_LOGO_SLOTS.map(({ logoField, label }) => (
          <LogoSlot
            key={logoField}
            label={label}
            current={currentLogoForUploadField(settings, logoField)}
            pendingChange={pendingChanges[logoField] ?? null}
            onPendingChange={(change) => onPendingChange(logoField, change)}
          />
        ))}
      </SlotsGrid>

      <SectionHint variant='caption' color='text.secondary'>
        PNG, JPEG, SVG, WEBP, ICO — max 2 MB. Click or drag onto a slot to
        choose a file.
      </SectionHint>
    </WhiteLabelSectionRoot>
  );
}

// --- Styled layout (matches other WhiteLabelSettings sections) ---

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SlotsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(4),
}));

const SectionHint = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: 'block',
}));

const SlotColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const SlotLabel = styled(Typography)({
  fontWeight: 600,
});

const DropZoneRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'hasImage',
})<{ isDragging: boolean; hasImage: boolean }>(
  ({ theme, isDragging, hasImage }) => ({
    position: 'relative',
    width: 96,
    height: 96,
    borderRadius: '50%',
    border: `1px ${hasImage && !isDragging ? 'solid' : 'dashed'} ${
      isDragging ? theme.palette.primary.main : theme.palette.grey[400]
    }`,
    backgroundColor: isDragging
      ? alpha(theme.palette.primary.main, 0.06)
      : theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    transition: 'border-color 0.2s, background-color 0.2s',
  }),
);

const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: 12,
});

const EmptySlotBody = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.disabled,
  padding: theme.spacing(1),
}));

const EmptySlotIconWrap = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const SlotActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const RemoveLogoButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textTransform: 'none',
  fontWeight: 400,
  '& .MuiButton-startIcon': { marginRight: '2px' },
}));

const UpdateLogoButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(5),
  textTransform: 'none',
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  fontWeight: 500,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const ChooseFileButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));
